import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import './BottomNav.css';

import homeIcon from '../../assets/home-icon.png';
import homedarkIcon from '../../assets/homedark-icon.png';

import searchIcon from '../../assets/search-icon.png';
import searchIconDark from '../../assets/searchdark-icon.png';

import postIcon from '../../assets/post-icon.png';
import postIconDark from '../../assets/postdark-icon.png';

import chatIcon from '../../assets/chat-icon.png';
import chatIconDark from '../../assets/chatdark-icon.png';

import placeholderProfile from '../../assets/placeholder-profile.png';

const MAIN_ROUTES = [
  '/home',
  '/search',
  '/notifications',
  '/chats',
  '/profile',
];

const TAB_POSITIONS = {
  '/home': 0,
  '/search': 1,
  '/chats': 3,
  '/profile': 4,
};

const ACTIVE_TAB_KEY = 'bf_bottom_nav_active_tab';

export default function BottomNav({
  user,
  profileSrc,
  unreadDmCount = 0,
  hidden = false,
  onBeforeNavigate,
}) {
  const { theme } = useTheme();
  const { pathname } = useLocation();

const targetTab = TAB_POSITIONS[pathname] ?? null;

const [indicatorTab, setIndicatorTab] = useState(() => {
  try {
    const saved = sessionStorage.getItem(ACTIVE_TAB_KEY);
    const previous = saved === null ? NaN : Number(saved);

    return [0, 1, 3, 4].includes(previous)
      ? previous
      : targetTab;
  } catch {
    return targetTab;
  }
});

useEffect(() => {
  if (targetTab === null) return;

  try {
    sessionStorage.setItem(ACTIVE_TAB_KEY, String(targetTab));
  } catch {}

  // Show the previous position before starting the slide.
  let secondFrame;

  const firstFrame = requestAnimationFrame(() => {
    secondFrame = requestAnimationFrame(() => {
      setIndicatorTab(targetTab);
    });
  });

  return () => {
    cancelAnimationFrame(firstFrame);

    if (secondFrame !== undefined) {
      cancelAnimationFrame(secondFrame);
    }
  };
}, [targetTab]);

  const isDark = theme === 'dark';



  const icons = {
    home: isDark ? homedarkIcon : homeIcon,
    search: isDark ? searchIconDark : searchIcon,
    post: isDark ? postIconDark : postIcon,
    messages: isDark ? chatIconDark : chatIcon,
  };

  const avatarSrc =
    profileSrc || user?.profilePic || placeholderProfile;

  const parsedCount = Number(unreadDmCount);

  const unreadCount = Number.isFinite(parsedCount)
    ? Math.max(0, Math.floor(parsedCount))
    : 0;

  // Prevent the bar appearing on Create Post or detail screens.
  if (!MAIN_ROUTES.includes(pathname)) {
    return null;
  }

  const handleNavigation = (event, destination) => {
    // Tapping the current destination keeps its current state.
    if (pathname === destination) {
      event.preventDefault();
      return;
    }

    // Screens can use this to save their scroll position.
    onBeforeNavigate?.(destination);
  };

  const destinationClass = (destination) =>
    `bf-bottom-nav-link ${
      pathname === destination ? 'is-active' : ''
    }`;

  return (
    <nav
      className={`bf-bottom-nav ${hidden ? 'is-hidden' : ''}`}
      aria-label="Main navigation"
      aria-hidden={hidden ? true : undefined}
    >

<span
  className="bf-bottom-nav-indicator"
  aria-hidden="true"
  style={{
    '--bf-active-fraction': ((indicatorTab ?? 0) + 0.5) / 5,
    opacity: targetTab === null ? 0 : 1,
  }}
/>
      <Link
        to="/home"
        className={destinationClass('/home')}
        aria-label="Home"
        aria-current={pathname === '/home' ? 'page' : undefined}
        tabIndex={hidden ? -1 : undefined}
        onClick={(event) => handleNavigation(event, '/home')}
      >
        <img
          src={icons.home}
          alt=""
          className="bf-bottom-nav-icon"
        />
      </Link>

      <Link
        to="/search"
        className={destinationClass('/search')}
        aria-label="Search"
        aria-current={pathname === '/search' ? 'page' : undefined}
        tabIndex={hidden ? -1 : undefined}
        onClick={(event) => handleNavigation(event, '/search')}
      >
        <img
          src={icons.search}
          alt=""
          className="bf-bottom-nav-icon"
        />
      </Link>

      <div className="bf-bottom-nav-create-slot">
        <Link
          to="/create-post"
          className="bf-bottom-nav-create"
          aria-label="Create post"
          tabIndex={hidden ? -1 : undefined}
          onClick={(event) =>
            handleNavigation(event, '/create-post')
          }
        >
          <img src={icons.post} alt="" />
        </Link>
      </div>

      <Link
        to="/chats"
        className={destinationClass('/chats')}
        aria-label={
          unreadCount > 0
            ? `Messages, ${unreadCount} unread`
            : 'Messages'
        }
        aria-current={pathname === '/chats' ? 'page' : undefined}
        tabIndex={hidden ? -1 : undefined}
        onClick={(event) => handleNavigation(event, '/chats')}
      >
        <span className="bf-bottom-nav-icon-wrap">
          <img
            src={icons.messages}
            alt=""
            className="bf-bottom-nav-icon"
          />

          {unreadCount > 0 && (
            <span
              className="bf-bottom-nav-badge"
              aria-hidden="true"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </span>
      </Link>

      <Link
        to="/profile"
        className={`${destinationClass('/profile')} bf-bottom-nav-profile`}
        aria-label="Profile"
        aria-current={pathname === '/profile' ? 'page' : undefined}
        tabIndex={hidden ? -1 : undefined}
        onClick={(event) => handleNavigation(event, '/profile')}
      >
        <img
          src={avatarSrc}
          alt=""
          className="bf-bottom-nav-avatar"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = placeholderProfile;
          }}
        />
      </Link>
    </nav>
  );
}
